import { useState, useEffect } from "react";
import { RecruiterShell } from "@/shell/RecruiterShell";
import { api } from "@/lib/api";
import type { ReportsData } from "@/lib/types";

// ── Design tokens ──────────────────────────────────────────────────────────────
const T = {
  ink: "#17131F",
  paper: "#FBFAFE",
  accent: "var(--accent)",
  accentLight: "rgba(122,107,224,0.12)",
  slate: "#7C7590",
  good: "#1A7A4A",
  warn: "#B5811F",
  white: "#FFFFFF",
  border: "#E8E4DC",
  navy: "#1A3552",
  grey: "#9CA3AF",
} as const;

const BAR_COLORS: Record<string, string> = {
  Invited: "var(--accent)",
  "In progress": "#B5811F",
  Completed: "#1A7A4A",
  Expired: "#9CA3AF",
};

const cardStyle: React.CSSProperties = {
  background: T.white,
  borderRadius: 12,
  padding: 24,
  boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
};

const monoLabel: React.CSSProperties = {
  fontFamily: "var(--font-mono, 'Geist Mono', monospace)",
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.12em",
  textTransform: "uppercase" as const,
  color: T.slate,
};

// ── Stat card ─────────────────────────────────────────────────────────────────
function StatCard({ value, label }: { value: string; label: string }) {
  return (
    <div style={cardStyle}>
      <p
        style={{
          margin: "0 0 8px",
          fontSize: 40,
          fontWeight: 700,
          lineHeight: 1,
          color: T.accent,
        }}
      >
        {value}
      </p>
      <p style={{ ...monoLabel, margin: 0 }}>{label}</p>
    </div>
  );
}

// ── Horizontal bar chart ──────────────────────────────────────────────────────
function HorizontalBarChart({
  rows,
  colorMap,
}: {
  rows: Array<{ label: string; count: number; pct: number }>;
  colorMap?: Record<string, string>;
}) {
  const maxCount = Math.max(...rows.map((r) => r.count), 1);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {rows.map((row) => {
        const barColor =
          (colorMap && colorMap[row.label]) ?? T.accent;
        const widthPct = maxCount > 0 ? (row.count / maxCount) * 100 : 0;

        return (
          <div
            key={row.label}
            style={{
              display: "grid",
              gridTemplateColumns: "110px 1fr 36px",
              alignItems: "center",
              gap: 10,
            }}
          >
            {/* Label */}
            <span
              style={{
                fontSize: 12.5,
                color: T.slate,
                whiteSpace: "nowrap" as const,
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {row.label}
            </span>

            {/* Bar track */}
            <div
              style={{
                height: 8,
                borderRadius: 999,
                background: "#F3F4F6",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  height: "100%",
                  width: `${widthPct}%`,
                  borderRadius: 999,
                  background: barColor,
                  transition: "width 0.3s ease",
                }}
              />
            </div>

            {/* Count */}
            <span
              style={{
                fontSize: 12.5,
                fontWeight: 600,
                color: T.ink,
                textAlign: "right" as const,
              }}
            >
              {row.count}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ── Weekly bar chart (SVG-based) ──────────────────────────────────────────────
function WeeklyBarChart({
  weeks,
}: {
  weeks: Array<{ week_label: string; count: number }>;
}) {
  const maxCount = Math.max(...weeks.map((w) => w.count), 1);
  const chartHeight = 100;
  const barWidth = 22;
  const gap = 8;
  const totalWidth = weeks.length * (barWidth + gap) - gap;

  return (
    <div style={{ overflowX: "auto" }}>
      <div style={{ minWidth: totalWidth + 20 }}>
        {/* Bars */}
        <svg
          width={totalWidth}
          height={chartHeight}
          style={{ display: "block", overflow: "visible" }}
        >
          {weeks.map((w, i) => {
            const x = i * (barWidth + gap);
            const filledH = maxCount > 0 ? (w.count / maxCount) * (chartHeight - 8) : 0;
            const emptyH = chartHeight - 8 - filledH;

            return (
              <g key={i} transform={`translate(${x}, 0)`}>
                {/* Empty portion */}
                {emptyH > 0 && (
                  <rect
                    x={0}
                    y={0}
                    width={barWidth}
                    height={emptyH}
                    rx={4}
                    fill="#F3F4F6"
                  />
                )}
                {/* Filled portion */}
                {filledH > 0 && (
                  <rect
                    x={0}
                    y={emptyH}
                    width={barWidth}
                    height={filledH}
                    rx={4}
                    fill={T.accent}
                  />
                )}
                {/* Count label */}
                {w.count > 0 && (
                  <text
                    x={barWidth / 2}
                    y={emptyH - 4}
                    textAnchor="middle"
                    fontSize={9}
                    fontWeight={700}
                    fill={T.slate}
                    fontFamily="var(--font-mono, 'Geist Mono', monospace)"
                  >
                    {w.count}
                  </text>
                )}
              </g>
            );
          })}
        </svg>

        {/* X-axis labels */}
        <div
          style={{
            display: "flex",
            gap: gap,
            marginTop: 6,
          }}
        >
          {weeks.map((w, i) => {
            // Full label ("May 25") only fits 22px on the first tick or when the
            // month changes; otherwise show just the day so nothing clips mid-glyph.
            const [month, day] = w.week_label.split(" ");
            const showMonth = i === 0 || weeks[i - 1].week_label.split(" ")[0] !== month;
            return (
              <div
                key={i}
                title={w.week_label}
                style={{
                  width: barWidth,
                  flexShrink: 0,
                  fontSize: 8.5,
                  color: T.slate,
                  textAlign: "center" as const,
                  fontFamily: "var(--font-mono, 'Geist Mono', monospace)",
                  whiteSpace: "nowrap" as const,
                  overflow: "hidden",
                  textOverflow: "clip",
                }}
              >
                {showMonth ? month : day}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Recent completions table ───────────────────────────────────────────────────
function RecentCompletionsCard({
  rows,
}: {
  rows: ReportsData["recent_completions"];
}) {
  const thStyle: React.CSSProperties = {
    padding: "0 8px 10px",
    textAlign: "left" as const,
    fontSize: 10,
    fontWeight: 700,
    color: T.slate,
    letterSpacing: "0.08em",
    textTransform: "uppercase" as const,
    borderBottom: `1px solid ${T.border}`,
    whiteSpace: "nowrap" as const,
    fontFamily: "var(--font-mono, 'Geist Mono', monospace)",
  };

  const tdStyle: React.CSSProperties = {
    padding: "12px 8px",
    verticalAlign: "middle" as const,
    fontSize: 13.5,
  };

  return (
    <div style={cardStyle}>
      <p style={{ ...monoLabel, margin: "0 0 16px" }}>RECENT COMPLETIONS</p>

      {rows.length === 0 ? (
        <p
          style={{
            margin: 0,
            fontSize: 13.5,
            color: T.slate,
            lineHeight: 1.5,
          }}
        >
          No completed sessions yet. Completions appear here with a link to each
          scorecard.
        </p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: 13.5,
            }}
          >
            <thead>
              <tr>
                <th style={thStyle}>Candidate</th>
                <th style={thStyle}>Role / Company</th>
                <th style={{ ...thStyle, textAlign: "right" as const }}>Score</th>
                <th style={{ ...thStyle, textAlign: "right" as const }}>Scorecard</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr
                  key={i}
                  style={{
                    borderBottom:
                      i < rows.length - 1 ? `1px solid ${T.border}` : "none",
                  }}
                >
                  <td style={{ ...tdStyle, fontWeight: 600, color: T.ink }}>
                    {row.name}
                  </td>
                  <td style={{ ...tdStyle, color: T.slate }}>
                    {row.role}
                    {row.company ? ` · ${row.company}` : ""}
                  </td>
                  <td
                    style={{
                      ...tdStyle,
                      fontWeight: 700,
                      color: T.good,
                      textAlign: "right" as const,
                      whiteSpace: "nowrap" as const,
                    }}
                  >
                    {row.score}
                  </td>
                  <td style={{ ...tdStyle, textAlign: "right" as const }}>
                    <a
                      href={row.scorecard_url}
                      style={{
                        color: T.accent,
                        textDecoration: "none",
                        fontWeight: 500,
                        fontSize: 13,
                        whiteSpace: "nowrap" as const,
                      }}
                    >
                      View scorecard →
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export function RecruiterReports() {
  const [data, setData] = useState<ReportsData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<ReportsData>("/recruiter/reports")
      .then(setData)
      .catch((err: Error) => setError(err.message ?? "Failed to load reports"));
  }, []);

  if (error) {
    return (
      <RecruiterShell activeNav="reports">
        <div style={{ padding: 32, color: "#B91C1C", fontSize: 14 }}>
          Error: {error}
        </div>
      </RecruiterShell>
    );
  }

  if (!data) {
    return (
      <RecruiterShell activeNav="reports">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 200,
            fontSize: 14,
            color: T.slate,
          }}
        >
          Loading…
        </div>
      </RecruiterShell>
    );
  }

  const pipelineWithColors = data.pipeline.map((row) => ({
    ...row,
    color: BAR_COLORS[row.label] ?? T.accent,
  }));

  return (
    <RecruiterShell activeNav="reports">
      {/* ── Page header ──────────────────────────────────────────────── */}
      <div style={{ marginBottom: 28 }}>
        <h1
          style={{
            margin: 0,
            fontSize: 26,
            fontWeight: 700,
            color: T.ink,
            lineHeight: 1.2,
          }}
        >
          Reports
        </h1>
        <p
          style={{
            margin: "6px 0 0",
            fontSize: 13.5,
            color: T.slate,
          }}
        >
          Candidate pipeline and preparation activity for {data.agency_name}
        </p>
      </div>

      {/* ── Stat cards row ───────────────────────────────────────────── */}
      <div
        className="reports-stat-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 16,
          marginBottom: 24,
        }}
      >
        <StatCard
          value={String(data.stats.invited)}
          label="CANDIDATES INVITED"
        />
        <StatCard
          value={String(data.stats.in_progress)}
          label="IN PROGRESS"
        />
        <StatCard
          value={String(data.stats.completed)}
          label="SESSIONS COMPLETED"
        />
        <StatCard
          value={`${data.stats.completion_rate_pct}%`}
          label="COMPLETION RATE"
        />
      </div>

      {/* ── Pipeline + Weekly invites ─────────────────────────────────── */}
      <div
        className="reports-two-col"
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 16,
          marginBottom: 16,
        }}
      >
        {/* Pipeline */}
        <div style={cardStyle}>
          <p style={{ ...monoLabel, margin: "0 0 20px" }}>PIPELINE</p>
          <HorizontalBarChart rows={pipelineWithColors} colorMap={BAR_COLORS} />
        </div>

        {/* Invites per week */}
        <div style={cardStyle}>
          <p style={{ ...monoLabel, margin: "0 0 20px" }}>INVITES PER WEEK</p>
          {data.weekly_chart.length === 0 ? (
            <p style={{ margin: 0, fontSize: 13.5, color: T.slate }}>
              No weekly data yet.
            </p>
          ) : (
            <WeeklyBarChart weeks={data.weekly_chart} />
          )}
        </div>
      </div>

      {/* ── By persona + by mode ─────────────────────────────────────── */}
      <div
        className="reports-two-col"
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 16,
          marginBottom: 16,
        }}
      >
        {/* By interviewer persona */}
        <div style={cardStyle}>
          <p style={{ ...monoLabel, margin: "0 0 20px" }}>
            BY INTERVIEWER PERSONA
          </p>
          {data.persona_breakdown.length === 0 ? (
            <p style={{ margin: 0, fontSize: 13.5, color: T.slate }}>
              No data yet.
            </p>
          ) : (
            <HorizontalBarChart rows={data.persona_breakdown} />
          )}
        </div>

        {/* By practice mode */}
        <div style={cardStyle}>
          <p style={{ ...monoLabel, margin: "0 0 20px" }}>BY PRACTICE MODE</p>
          {data.mode_breakdown.length === 0 ? (
            <p style={{ margin: 0, fontSize: 13.5, color: T.slate }}>
              No data yet.
            </p>
          ) : (
            <HorizontalBarChart rows={data.mode_breakdown} />
          )}
        </div>
      </div>

      {/* ── Recent completions (full width) ──────────────────────────── */}
      <RecentCompletionsCard rows={data.recent_completions} />

      {/* ── Responsive rules ─────────────────────────────────────────── */}
      <style>{`
        @media (max-width: 720px) {
          .reports-stat-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          .reports-two-col {
            grid-template-columns: 1fr !important;
          }
        }
        @media (max-width: 400px) {
          .reports-stat-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </RecruiterShell>
  );
}
